import { z } from 'zod';

/**
 * M7 — Responder.
 *
 * ── La forma del envío ──
 * Se manda **todo el cuestionario de una vez**, no pregunta por pregunta. Un
 * envío parcial guardado dejaría respuestas a medias que los agregados de M8
 * contarían como opiniones — y una respuesta es un hecho: existe completa o no
 * existe. El teléfono guarda el avance EN MEMORIA mientras la persona navega;
 * la base solo conoce el envío final.
 *
 * Cada elemento apunta a su pregunta; la matriz manda un elemento POR
 * AFIRMACIÓN (`itemId`), y la selección múltiple un elemento POR OPCIÓN
 * marcada (`opcionId`). El servidor valida contra la versión congelada — esto
 * de acá solo garantiza la forma.
 */

export const esquemaValorRespuesta = z
  .object({
    preguntaId: z.number().int().positive(),
    /** La afirmación de una matriz. */
    itemId: z.number().int().positive().optional(),
    /** La opción elegida en una selección. */
    opcionId: z.number().int().positive().optional(),
    punto: z.number().int().positive().optional(),
    valorBool: z.boolean().optional(),
    texto: z.string().trim().min(1).max(5000).optional(),
    numero: z.number().int().optional(),
    /** aaaa-mm-dd. */
    fecha: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha va como aaaa-mm-dd.')
      .optional(),
    /**
     * El nombre del campo del `multipart` que trae el archivo de esta pregunta
     * (034). **No es el archivo ni su nombre original**: es un apuntador dentro
     * del mismo envío, del estilo `adjunto_3`.
     *
     * ⚠️ El nombre original del archivo **no viaja y no se guarda**. Un
     * `IMG_20260907_reunion_con_maria.jpg` es dato personal en el nombre, y
     * guardarlo metería en la base justo lo que el reprocesado saca del
     * contenido. El nombre en disco lo pone el servidor (UUID).
     */
    campoArchivo: z
      .string()
      .regex(/^adjunto_[1-9][0-9]{0,8}$/, 'El apuntador del adjunto no tiene la forma esperada.')
      .optional(),
    /** Duración de una nota de voz, en segundos. La mide quien graba. */
    segundos: z.number().int().positive().max(600).optional(),
    /**
     * Marca este elemento como **la observación abierta** de su pregunta (035),
     * no como su respuesta.
     *
     * ⚠️ Va como un elemento APARTE con su propio `texto`, y no como un campo
     * extra del elemento de la respuesta. Es lo que exige el modelo: en la base
     * son dos filas de `respuesta_detalles` —el punto elegido y el texto— porque
     * `ck_detalle_un_valor` admite exactamente un valor por fila.
     */
    esObservacion: z.boolean().optional(),
  })
  .refine(
    (v) =>
      [v.opcionId, v.punto, v.valorBool, v.texto, v.numero, v.fecha, v.campoArchivo].filter(
        (x) => x !== undefined,
      ).length === 1,
    'Cada elemento lleva exactamente un valor.',
  )
  .refine((v) => v.segundos === undefined || v.campoArchivo !== undefined, {
    message: 'Los segundos solo acompañan a un archivo.',
    path: ['segundos'],
  })
  .refine((v) => v.esObservacion !== true || v.texto !== undefined, {
    message: 'Una observación es texto.',
    path: ['esObservacion'],
  });
export type ValorRespuesta = z.infer<typeof esquemaValorRespuesta>;

export const esquemaEnviarRespuestas = z.object({
  respuestas: z.array(esquemaValorRespuesta).min(1).max(1000),
});

/**
 * ── Cómo viaja un envío con adjuntos (034) ──
 * Como **un solo `multipart/form-data`**: un campo `datos` con este JSON, más un
 * campo por archivo (`adjunto_1`, `adjunto_2`…) apuntado desde `campoArchivo`.
 *
 * ⚠️ **Un solo request, no dos pasos.** La alternativa —subir los archivos
 * primero, recibir unos tokens y después enviar el cuestionario— rompe lo que la
 * 019 sostiene: una respuesta **existe completa o no existe**. Con dos pasos, un
 * teléfono que se queda sin batería entre uno y otro deja archivos subidos que no
 * son respuesta de nada, y hay que inventar quién los barre. Con un request, la
 * transacción decide: o entra todo, o no entró nada.
 */
export const NOMBRE_CAMPO_DATOS = 'datos';
export type EntradaEnviarRespuestas = z.infer<typeof esquemaEnviarRespuestas>;

export const esquemaRespuestaEnviada = z.object({
  enviadoEn: z.string(),
});
export type RespuestaEnviada = z.infer<typeof esquemaRespuestaEnviada>;
